<template>
  <span class="promo-campaign" :class="{ 'promo-campaign--thin': usedInCatalog }">
    <span class="promo-campaign__title">{{ $t('common.discount') }}</span>

    <span class="promo-campaign__percent">
      {{ offerDiscount }}%
    </span>

    <span
      class="promo-campaign__time"
      :class="{
        'promo-campaign__time--lowercased': isTodayEnd,
      }"
    >
      {{ discountTime }}
    </span>
  </span>
</template>

<script setup lang="ts">
type Props = {
  finishesAt?: string,
  price?: number,
  priceOld: number | null
  usedInCatalog?: boolean,
}

const props = defineProps<Props>()
const i18n = useI18n()
const { $dayjs } = useNuxtApp()

const offerDiscount = getProductDiscount(Number(props.price), Number(props.priceOld))

const isTodayEnd = $dayjs(props.finishesAt).diff($dayjs(), 'hour') < 24
const isEndInHour = $dayjs(props.finishesAt).diff($dayjs(), 'hour') < 1
const targetDate = $dayjs(props.finishesAt)

const discountTime = shallowRef<string>(`${i18n.t('offers.is_valid_until')} ${targetDate.format('D MMMM')}`)
const formatTime = (hoursLeft: number, minutesLeft: number, secondsLeft: number) => {
  if (isEndInHour) {
    return `${minutesLeft < 10 ? '0' : ''}${minutesLeft} ${i18n.t('common.minutes_shorted')} : ${secondsLeft < 10 ? '0' : ''}${secondsLeft} ${i18n.t('common.seconds_shorted')}`
  }
  return `${hoursLeft} ${i18n.t('common.hour_shorted')} : ${minutesLeft < 10 ? '0' : ''}${minutesLeft} ${i18n.t('common.minutes_shorted')}`
}

const updateInterval = shallowRef<ReturnType<typeof setInterval>>()
const updateTime = () => {
  const now = $dayjs();

  const hoursLeft = targetDate.diff(now, 'hour');
  const minutesLeft = targetDate.diff(now, 'minute') % 60;
  const secondsLeft = targetDate.diff(now, 'seconds') % 60;

  discountTime.value = formatTime(hoursLeft, minutesLeft, secondsLeft)
  if (hoursLeft < 1 && minutesLeft < 1 && secondsLeft < 1) {
    clearInterval(updateInterval.value)
    discountTime.value = i18n.t('offers.promo_campaign_expired') as string
  }
}

onMounted(() => {
  if (isEndInHour) {
    updateTime()
    updateInterval.value = setInterval(updateTime, 1000)
  } else if (isTodayEnd) {
    updateTime()
    updateInterval.value = setInterval(updateTime, 60000)
  }
})
onBeforeUnmount(() => {
  clearInterval(updateInterval.value)
})
</script>

<style lang="scss" scoped>
.promo-campaign {
  width: fit-content;

  font-size: 1.2rem;
  line-height: 1.8rem;
  color: $label-red-color;

  background-color: #fde8e9;
  border-radius: 0.8rem;

  padding: 0.4rem 0.8rem;

  &__title {
    text-transform: uppercase;
    font-weight: 700;
    margin-right: 3px;
  }

  &__percent {
    background-color: $white-color;
    border-radius: 0.6rem;
    line-height: 1.2rem;
    font-weight: 700;

    padding: 2px 1px 2px 3px;

    margin-right: 7px;
    white-space: nowrap;
  }

  &__time {
    font-weight: 500;
    color: $font-color;
    white-space: nowrap;

    &--lowercased {
      text-transform: lowercase;
    }
  }

  &--thin {
    @media only screen and (max-width: 740px) {
      font-size: 0.8rem;
      padding: 0.2rem 0.4rem;
      border-radius: 0.5rem;

      line-height: 1.2rem;

      & > .promo-campaign__percent {
        border-radius: 0.5rem;
        padding: 0.1rem 0.2rem;
      }
    }
  }
}
</style>
